import { memo, useEffect, useState, useCallback } from 'react'
import { Helmet } from 'react-helmet-async'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { History } from 'history'
import {
  IconInfoInverted,
  Box,
  useDialogControllers,
  useResponsiveValue,
  Pagination
} from '@machobear/design-system'

import { colors, breakpoints, structure } from '@/styles'

import {
  Table,
  TableHeader,
  TableHeaderCell,
  TableBody,
  TableRow,
  StyledTableCell,
  SingleTableRow,
  TableHeaderText
} from '@/components/listings-table'

import useDeleteTrustScoreDraft from '@/hooks/use-delete-standalone-trustscore'
import useFavouriteTrustScore from '@/hooks/use-favourite-trustscore'
import useGetTrustScoreList from '@/hooks/use-search-standalone-trust-score'
import useMediaQuery from '@/hooks/use-media-query'
import queryClient from '@/components/query-client'

import PageTitle from '@/components/page-title'
import { toUnit } from '@/utils/styles'
import useLocalStorage from '@/hooks/use-localstorage'
import config from '@/config'
import NoBillingUserDialog from '@/features/SponsoredListing/modals/no-billing-user-modal'
import useCurrentSession from '@/hooks/use-current-session'
import { CreditCheckDialog } from './modals/credit-check-dialog'
import { DeleteTrustScoreDialog } from './modals/delete-trust-score-dialog'
import { MobileTrustScoreRow } from './components/mobile-table-row'
import { getTrustScoreFavouriteHeader } from './components/favourite-column'
import TrustScoreHeader from './components/trustscore-header'
import { MobileButton } from './components/mobile-footer-button'
import TrustScoreEmptyComponent from './components/empty-trustscore'
import TrustScoreTopper from './components/trustscore-topper'
import SkeletonState from './components/skeleton-state'
import { TrustScoreRow } from './components/table-row'
import { Modals } from './types'
import { updateCurrentSort } from './utils'
import { HeartImage } from './images/heart-image'

const { APP_PATH } = config

const Wrapper = styled.div`
  min-height: calc(100vh - ${toUnit(structure.header.height)});
  background-color: ${colors.white};
  padding: 0 0 120px;
  flex: 1;
  display: flex;
  flex-direction: column;

  background-repeat: no-repeat;
  background-position: 100% 100%;
  background-size: 542px;

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    min-height: calc(100vh - ${toUnit(structure.header.heightMobile)});
    padding-bottom: 160px;
  }
`

const CustomSingleTableRow = styled(SingleTableRow)<{ status: string }>`
  cursor: ${props => (props.status === 'pending' ? 'default' : 'pointer')};
`

const TableOutterWrapper = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
`

const TableWrapper = styled.div`
  flex: 1;
  margin: 0 40px;

  @media only screen and (max-width: ${breakpoints.ipadMiniMax}) {
    margin: 0;
  }
`

const TrustScoreTable = styled(Table)`
  width: 100%;
  border: 1px solid ${colors.mediumLightGrey};
  box-shadow: 0 2px 8px 0 ${colors.mediumLightGrey};

  @media only screen and (max-width: ${breakpoints.extraLargeDesktop}) {
    border: none;
    border-bottom: 1px solid ${colors.mediumLightGrey};
    box-shadow: none;
  }
`

const StandaloneTrustScoreList = ({ history }: { history: History }) => {
  const { t } = useTranslation()
  const session = useCurrentSession()

  const { visible, showModal, closeModal } = useDialogControllers({
    creditCheckDialog: false,
    deleteTrustScoreDialog: false,
    billingUser: false
  })

  const displayDesktop = useMediaQuery(`(max-width: ${breakpoints.desktop})`)
  const isMobile = useMediaQuery(`(max-width: ${breakpoints.ipadMiniMax})`)

  const centerPagination = useResponsiveValue()({
    mobile: true,
    tablet: true,
    desktop: false
  })

  const [currentTab, setCurrentTab] = useState('all')
  const [page, setPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)
  const [currentSearchString, setCurrentSearchString] = useState('')

  const [tabTrustScoreData, setTabTrustScoreData] = useState([])
  const [currentSort, setCurrentSort] = useState({
    column: 'default',
    direction: 'desc'
  })

  const [deleteId, setDeleteId] = useState('')
  const [deleting, setDeleting] = useState(false)

  const { data: originalTrustScoreData } = useGetTrustScoreList({
    currentTab: 'all',
    searchString: currentSearchString,
    page: page,
    limit: itemsPerPage,
    sortBy: currentSort.column === 'default' ? 'last_updated' : currentSort.column,
    sortOrder: currentSort.direction
  })

  const {
    data: trustScoreData,
    isLoading,
    isFetching,
    isRefetching
  } = useGetTrustScoreList({
    currentTab: currentTab,
    searchString: currentSearchString,
    page: page,
    limit: itemsPerPage,
    sortBy: currentSort.column === 'default' ? 'last_updated' : currentSort.column,
    sortOrder: currentSort.direction
  })

  const handleStartScreening = () => {
    const isBillingUserValid = session?.billing_user_valid
    isBillingUserValid ? history.push(`/trust-score/credit-check`) : showModal('billingUser')()
  }

  const deleteDraft = useDeleteTrustScoreDraft()

  const deleteTrustScoreMethod = async () => {
    try {
      const response = await deleteDraft.mutateAsync({ id: deleteId })

      if (tabTrustScoreData.length === 1 && page > 0) {
        setDeleting(true)
        setPage(page - 1)
      } else {
        queryClient.invalidateQueries({
          queryKey: ['trustScoreList', currentTab, currentSearchString, page, itemsPerPage],
          exact: false
        })
      }

      return response
    } catch (error) {
      console.error('Error deleting trust score data', error)
    }
  }

  const favouriteTrustScore = useFavouriteTrustScore({
    currentTab: currentTab,
    searchString: currentSearchString,
    page: page,
    limit: itemsPerPage
  })

  const handleSetTab = useCallback(
    (machineStates: any) => {
      const filtered = trustScoreData?.items
        .map((trustScore: any) => ({
          ...trustScore
        }))
        .filter((trustScore: any) => machineStates.includes(trustScore.status))
      setTabTrustScoreData(filtered)
    },
    [trustScoreData]
  )

  useEffect(() => {
    if (currentTab === 'all') {
      handleSetTab(['completed', 'pending', 'failed', 'draft', 'expired'])
    } else if (currentTab === 'completed') {
      handleSetTab(['completed', 'pending'])
    } else if (currentTab === 'failed') {
      handleSetTab(['failed'])
    } else {
      handleSetTab(['draft', 'expired'])
    }
  }, [currentTab, handleSetTab])

  useEffect(() => {
    if (deleting) {
      queryClient.invalidateQueries({
        queryKey: ['trustScoreList', currentTab, currentSearchString, page, itemsPerPage],
        exact: false
      })
      setDeleting(false)
    }
  }, [page])

  const searchTrustScoreData = async (searchString: string) => {
    setCurrentSearchString(searchString)
  }

  const onItemsPerPageChange = (item: number) => {
    setPage(1)
    setItemsPerPage(item)
  }

  return (
    <Wrapper>
      <Helmet>
        <title>{t('l.standalone_trustscore.list.title', 'Trust Score')}</title>
      </Helmet>
      <PageTitle>{t('l.standalone_trustscore.list.title', 'Trust Score')}</PageTitle>

      {!isMobile && <TrustScoreTopper />}

      <TrustScoreHeader
        tabCount={originalTrustScoreData?.counters}
        currentTab={currentTab}
        setPage={setPage}
        setCurrentTab={setCurrentTab}
        onSearch={searchTrustScoreData}
      />

      <TableOutterWrapper>
        <TableWrapper>
          <TrustScoreTable>
            {!isMobile && (
              <TableHeader>
                <TableRow sticky top={!displayDesktop ? 186 : !isMobile ? 161 : 124}>
                  <TableHeaderCell width="7%">
                    <TableHeaderText>
                      <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        paddingX="small"
                      >
                        <HeartImage src={getTrustScoreFavouriteHeader(false)} />
                      </Box>
                    </TableHeaderText>
                  </TableHeaderCell>
                  <TableHeaderCell
                    onClick={() => {
                      updateCurrentSort('name', currentSort, setCurrentSort)
                    }}
                    active={currentSort.column === 'name'}
                    ascending={currentSort.direction === 'asc'}
                    alpha
                    width="25%"
                  >
                    <TableHeaderText alignLeft>
                      {t('l.standalone_trustscore.list.table.header_cell.name', 'Name')}
                    </TableHeaderText>
                  </TableHeaderCell>
                  <TableHeaderCell
                    onClick={() => {
                      updateCurrentSort('last_updated', currentSort, setCurrentSort)
                    }}
                    active={currentSort.column === 'last_updated'}
                    ascending={currentSort.direction === 'asc'}
                    width="20%"
                  >
                    <TableHeaderText margin={'0 15px 0 0'} alignLeft>
                      {t('l.standalone_trustscore.list.last_updated.text', 'Last Updated,')}
                    </TableHeaderText>
                  </TableHeaderCell>
                  <TableHeaderCell
                    onClick={() => {
                      updateCurrentSort('trust_score', currentSort, setCurrentSort)
                    }}
                    active={currentSort.column === 'trust_score'}
                    ascending={currentSort.direction === 'asc'}
                    width="20%"
                  >
                    <TableHeaderText margin={'0 15px 0 0'} alignLeft>
                      {t(
                        'l.standalone_trustscore.list.table.header_cell.trust_score',
                        'Trust Score'
                      )}
                    </TableHeaderText>
                  </TableHeaderCell>
                  <TableHeaderCell
                    onClick={() => {
                      updateCurrentSort('status', currentSort, setCurrentSort)
                    }}
                    active={currentSort.column === 'status'}
                    ascending={currentSort.direction === 'asc'}
                    width="30%"
                  >
                    <TableHeaderText margin={'0 15px 0 0'} alignLeft>
                      <Box display="flex" flexDirection="row" alignItems="center" gap="xxxsmall">
                        {t('l.standalone_trustscore.list.table.header_cell.credit_check', 'Status')}
                        <Box
                          onClick={e => {
                            e.stopPropagation()
                            showModal('creditCheckDialog')()
                          }}
                        >
                          <IconInfoInverted
                            title="credit-check-click"
                            titleId="credit-check-click"
                          />
                        </Box>
                      </Box>
                    </TableHeaderText>
                  </TableHeaderCell>
                  <TableHeaderCell width="140px">
                    <TableHeaderText>&nbsp;</TableHeaderText>
                  </TableHeaderCell>
                  <TableHeaderCell width="64px">
                    <TableHeaderText>&nbsp;</TableHeaderText>
                  </TableHeaderCell>
                </TableRow>
              </TableHeader>
            )}
            <TableBody>
              {!isLoading ? (
                <>
                  {tabTrustScoreData?.length > 0 ? (
                    tabTrustScoreData?.map((trustScoreInfo: { id: string; status: string }) => (
                      <TableRowWithData
                        trustScoreInfo={trustScoreInfo}
                        key={trustScoreInfo.id}
                        history={history}
                        showModal={showModal}
                        isMobile={isMobile}
                        setDeleteId={setDeleteId}
                        favouriteTrustScore={favouriteTrustScore}
                      />
                    ))
                  ) : currentSearchString?.length > 0 ? (
                    <SingleTableRow nonClickable>
                      <StyledTableCell colSpan={8}>
                        <TrustScoreEmptyComponent />
                      </StyledTableCell>
                    </SingleTableRow>
                  ) : !isFetching && !isRefetching ? (
                    <SingleTableRow nonClickable>
                      <StyledTableCell colSpan={8}>
                        <TrustScoreEmptyComponent />
                      </StyledTableCell>
                    </SingleTableRow>
                  ) : (
                    <SkeletonState />
                  )}
                </>
              ) : (
                <SkeletonState />
              )}
            </TableBody>
          </TrustScoreTable>
        </TableWrapper>
      </TableOutterWrapper>
      <Box
        display="flex"
        justifyContent={centerPagination ? 'center' : 'flexEnd'}
        paddingY="gutter"
        paddingX="xxxlarge"
        position="relative"
      >
        {trustScoreData?.total_items > 0 && tabTrustScoreData?.length > 0 && (
          <Pagination
            page={page}
            totalItems={trustScoreData?.total_items}
            itemsPerPage={itemsPerPage}
            itemsPerPageOptions={[10, 20, 30, 40, 50]}
            onItemsPerPageChange={onItemsPerPageChange}
            linkProps={({ page }) => ({
              href: `#`,
              onClick: e => {
                e.preventDefault()
                setPage(page)
              }
            })}
            label="Pagination"
            previousLabel="Previous"
            nextLabel="Next"
          />
        )}
      </Box>
      {isMobile && <MobileButton handleStartScreening={handleStartScreening} />}
      <CreditCheckDialog
        open={visible.creditCheckDialog}
        onClose={closeModal('creditCheckDialog')}
      />
      <DeleteTrustScoreDialog
        open={visible.deleteTrustScoreDialog}
        onClose={closeModal('deleteTrustScoreDialog')}
        onDelete={deleteTrustScoreMethod}
      />
      <NoBillingUserDialog open={visible.billingUser} onClose={closeModal('billingUser')} />
    </Wrapper>
  )
}

const TableRowWithData = ({
  trustScoreInfo,
  history,
  isMobile,
  showModal,
  setDeleteId,
  favouriteTrustScore
}: {
  trustScoreInfo: { id: string; status: string }
  history: History
  isMobile: boolean
  showModal: (modal: keyof Modals) => () => void
  setDeleteId: (deleteId: string) => void
  favouriteTrustScore: () => void
}) => {
  const [leftFormType = 'credit-check'] = useLocalStorage(
    `standalone-trustscore-${trustScoreInfo.id}`
  )
  const session = useCurrentSession()

  const isBillingUserValid = session?.billing_user_valid

  return (
    <CustomSingleTableRow
      role="row"
      key={trustScoreInfo?.id}
      status={trustScoreInfo.status}
      onClick={e => {
        e.stopPropagation()
        const isPending = trustScoreInfo?.status === 'pending'

        if (isPending) return
        if (!isBillingUserValid) return showModal('billingUser')()

        const url =
          trustScoreInfo.status === 'draft'
            ? `/trust-score/edit/${trustScoreInfo.id}/${leftFormType}`
            : `/trust-score/view/${trustScoreInfo.id}`
        window.open(`${APP_PATH}${url}`, '_blank')
      }}
    >
      {isMobile ? (
        <MobileTrustScoreRow
          trustScoreInfo={trustScoreInfo}
          showModal={showModal}
          setDeleteId={setDeleteId}
          favouriteTrustScore={favouriteTrustScore}
        />
      ) : (
        <TrustScoreRow
          isBillingUserValid={isBillingUserValid}
          trustScoreInfo={trustScoreInfo}
          showModal={showModal}
          setDeleteId={setDeleteId}
          favouriteTrustScore={favouriteTrustScore}
        />
      )}
    </CustomSingleTableRow>
  )
}

export default memo(StandaloneTrustScoreList)
